import React from "react";
import { PolyIcon, ToolTip } from "elements";
import { Link } from "polyrouter";
import { Card } from "collections/Cards";
import { trackInterest } from "monitoring";
import { humanReadable } from "utilities/numberUtils";

export interface Metric {
  value?: string | number;
  label?: string;
  size?: " " | "large";
}

/**
 * Meta metrics are either extended metrics, or meta.
 * Both are hidden outside of hover.
 * Meta is less important text, typically attached to another metric, like secondary.
 */
export interface Meta extends Metric {
  kind: "meta" | "extended";
}
/**
 * Nothing special about notes.  They are always muted in style.  (+4 more)...
 */
export interface Note extends Metric {
  kind: "note";
}
/**
 * A secondary metric consists of a statistic and attached meta information.
 */
export interface SecondaryMetric extends Metric {
  kind: "secondary";
  meta?: Meta;
}
export type SecondaryMetrics = SecondaryMetric | Meta | Note;

export interface NavCardProps {
  icon?: string;
  linkTo: string;
  primaryMetric: Metric;
  secondary?: Array<SecondaryMetrics>;
  showAll?: boolean;
  "data-testid"?: string;
}

export interface CardContentProps {
  metric: SecondaryMetrics;
}

export interface CardMetricProps {
  metricDisplayValue: string | undefined;
  metricValue: string | number | undefined;
  showTooltip: boolean;
}

/**
 * Large primary metric displayed at the top of the card.
 *
 * @param {string | undefined} metricDisplayValue
 * @param {string | number | undefined } metricValue
 */
export const CardMetric: React.FC<CardMetricProps> = ({
  metricDisplayValue,
  metricValue,
  showTooltip
}) => {
  return (
    <div className="metric">
      <ToolTip
        iconReplacement={<></>}
        iconPosition="right"
        iconTitle="info"
        size="xs"
        position="bottom"
        title={metricDisplayValue}
        disablePopup={!showTooltip}
      >
        <div className="metric-tooltip">{metricValue}</div>
      </ToolTip>
    </div>
  );
};

/**
 * Text just below large primary metric (eg, "Inventory", "Actions", "Priority Insights")
 *
 * @param {ReactNode} children
 */
export const CardLabel: React.FC = ({ children }) => {
  return <div className="label">{children}</div>;
};

export const SecondaryLine: React.FC<SecondaryMetric> = ({ meta, ...rest }) => {
  return (
    <>
      <DefaultLine {...rest} />
      {meta && <MetaMetric {...meta} />}
    </>
  );
};

export const DefaultLine: React.FC<SecondaryMetrics> = ({ kind, label }) => {
  return <p className={`detail-default detail-${kind}`}>{label}</p>;
};

/**
 * This is rendered within the "Card details" section, typically second / third (see diagram at bottom)
 *
 * @param {string} kind
 * @param {string} value
 * @param {string} label
 */
export const MetaMetric: React.FC<Meta> = ({ kind, value, label }) => {
  return (
    <p className={`detail-meta extended ${kind}`} key={value + "-id"}>
      {value + " " + (label || "")}
    </p>
  );
};

/**
 * This is rendered within the "Card details" section, typically second / third (see diagram at bottom)
 *
 * @param {string} kind
 * @param {string} value
 * @param {string} label
 */
export const NoteMetric: React.FC<Note> = ({ kind, value, label }) => {
  return (
    <p className={`detail-note ${kind}`} key={value + "-id"}>
      {value + " " + (label || "")}
    </p>
  );
};

export const CardContent: React.FC<CardContentProps> = ({ metric }) => {
  switch (metric.kind) {
    case "meta":
      return <MetaMetric {...metric} />;
    case "extended":
      return <MetaMetric {...metric} />;
    case "secondary":
      return <SecondaryLine {...metric} />;
    case "note":
      return <NoteMetric {...metric} />;
    default:
      return <DefaultLine {...metric} />;
  }
};

export const HomeCard: React.FC<NavCardProps> = ({
  primaryMetric,
  icon,
  linkTo,
  secondary,
  ...rest
}) => {
  const metricOutput = humanReadable(primaryMetric.value);
  return (
    <Card data-testid={rest["data-testid"]}>
      <Link
        to={linkTo}
        onClick={trackInterest(primaryMetric.label!, "HomeTile")}
      >
        <div className="metrics" data-testid="metric-card">
          <CardMetric
            metricDisplayValue={metricOutput.hrOutput}
            metricValue={metricOutput.fmtOutput}
            showTooltip={metricOutput.isAbbreviated}
          />
          <CardLabel>{primaryMetric.label}</CardLabel>
        </div>
        <div className="card-details">
          {secondary?.map((s, idx) => (
            <CardContent metric={s} key={s.label + ":" + idx} />
          ))}
        </div>
        {icon ? (
          <PolyIcon
            icon={icon}
            title={primaryMetric.label}
            className="card-icon"
          />
        ) : null}
      </Link>
    </Card>
  );
};

/**
 * As rendered on Home Page:
 * ----------------------------------------
 * |                                      |
 * |            #####   #####             | -
 * |            #       #                 |  \
 * |            ####    ####              |  |- .metric (<CardMetric />)
 * |                #       #             |  /
 * |            ####    ####              | -
 * |                                      |
 * |               Actions                | <-- .label (<CardLabel />)
 * |                                      |
 * |         213 Offline devices          | <-- .detail-default.detail-secondary (<SecondaryLine />) -
 * |                G7500                 | <-- .detail-meta (<MetaMetric />)                         |- .card-details
 * |              +15 more                | <-- .detail-note (<NoteMetric />)                        -
 * |                                      |
 * |                                  []  | <-- .card-icon.poly-icon
 * ----------------------------------------
 */
